import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';

const SubscriptionComplete = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentProcessing, setPaymentProcessing] = useState(true);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    console.log('=== SubscriptionComplete Page Loaded ===');
    console.log('Session ID from URL:', sessionId);
    console.log('Full URL:', window.location.href);

    if (sessionId) {
      handleStripeCallback(sessionId);
    } else {
      console.error('No session_id found in URL');
      setError('No session ID found. Invalid payment callback.');
      setPaymentProcessing(false);
    }
  }, [searchParams]);

  const handleStripeCallback = async (sessionId) => {
    console.log('=== Starting Payment Verification ===');
    console.log('Session ID:', sessionId);

    try {
      setPaymentProcessing(true);
      setError('');

      console.log('Calling backend to verify payment...');
      const response = await authService.handlePaymentSuccess(sessionId);

      console.log('Backend response:', response);

      if (response && response.status) {
        console.log('✅ Payment verified successfully!');
        setPaymentSuccess(true);

        // Update user in localStorage
        const currentUser = authService.getCurrentUser();
        if (currentUser && response.user) {
          const updatedUser = { ...currentUser, ...response.user };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          console.log('User updated:', updatedUser);
        }

        // Redirect after 3 seconds
        console.log('Redirecting to onboarding in 3 seconds...');
        setTimeout(() => {
          navigate('/onboarding/organization');
        }, 3000);
      } else {
        console.error('❌ Payment verification failed');
        setError('Payment verification failed. Please contact support.');
        setPaymentProcessing(false);
      }
    } catch (err) {
      console.error('❌ Error during payment verification:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to verify payment. Please contact support.';
      setError(errorMessage);
      setPaymentProcessing(false);
    }
  };

  // Loading screen
  if (paymentProcessing && !error) {
    return (
      <div style={styles.container}>
        <div style={styles.content}>
          <div style={styles.spinner}></div>
          <h2 style={styles.title}>Processing Your Payment</h2>
          <p style={styles.subtitle}>Please wait while we verify your payment...</p>
        </div>
        <style>{spinnerKeyframes}</style>
      </div>
    );
  }

  // Success screen
  if (paymentSuccess && !error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>
            <svg style={styles.checkIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 style={styles.successTitle}>Payment Successful!</h2>
          <p style={styles.successText}>Your subscription has been activated.</p>
          <p style={styles.redirectText}>Redirecting you to complete your profile...</p>
          <div style={styles.progressBar}>
            <div style={styles.progressFill}></div>
          </div>
        </div>
        <style>{progressKeyframes}</style>
      </div>
    );
  }

  // Error screen
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>
            <svg style={styles.xIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 style={styles.errorTitle}>Payment Error</h2>
          <p style={styles.errorText}>{error}</p>
          <button onClick={() => navigate('/pricing-plans')} style={styles.button}>
            Back to Pricing Plans
          </button>
        </div>
      </div>
    );
  }

  // Fallback
  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <p style={styles.subtitle}>Loading...</p>
      </div>
    </div>
  );
};

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    padding: '20px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    textAlign: 'center',
  },
  spinner: {
    width: '60px',
    height: '60px',
    border: '5px solid #e5e7eb',
    borderTop: '5px solid #1E88E5',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#111827',
    marginBottom: '8px',
    fontFamily: 'Poppins, sans-serif',
  },
  subtitle: {
    fontSize: '16px',
    color: '#6b7280',
    fontFamily: 'Poppins, sans-serif',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    backgroundColor: '#d1fae5',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  checkIcon: {
    width: '40px',
    height: '40px',
    color: '#10b981',
  },
  successTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '12px',
    fontFamily: 'Poppins, sans-serif',
  },
  successText: {
    fontSize: '16px',
    color: '#4b5563',
    marginBottom: '12px',
    fontFamily: 'Poppins, sans-serif',
  },
  redirectText: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '24px',
    fontFamily: 'Poppins, sans-serif',
  },
  progressBar: {
    width: '100%',
    height: '4px',
    backgroundColor: '#e5e7eb',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#1E88E5',
    animation: 'progress 3s linear',
    width: '0%',
  },
  errorIcon: {
    width: '80px',
    height: '80px',
    backgroundColor: '#fee2e2',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
  },
  xIcon: {
    width: '40px',
    height: '40px',
    color: '#ef4444',
  },
  errorTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '12px',
    fontFamily: 'Poppins, sans-serif',
  },
  errorText: {
    fontSize: '16px',
    color: '#ef4444',
    marginBottom: '24px',
    fontFamily: 'Poppins, sans-serif',
  },
  button: {
    padding: '12px 24px',
    backgroundColor: '#1E88E5',
    color: 'white',
    borderRadius: '8px',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '16px',
    fontWeight: '600',
    transition: 'background-color 0.2s',
  },
};

const spinnerKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const progressKeyframes = `
  @keyframes progress {
    0% { width: 0%; }
    100% { width: 100%; }
  }
`;

export default SubscriptionComplete;
